import React, { useEffect, useState, useCallback, useRef } from "react";
import Select, { components } from "react-select";
import QueryService from "../../../services/QueryService";
import dropdownIcon from "../../../assets/images/advanceQuery/dropdown.png";

// Comprehensive ResizeObserver error suppression
const suppressResizeObserverError = () => {
  // Suppress webpack overlay
  const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
  const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
  if (resizeObserverErr) {
    resizeObserverErr.setAttribute('style', 'display: none');
  }
  if (resizeObserverErrDiv) {
    resizeObserverErrDiv.setAttribute('style', 'display: none');
  }
  
  // Also check for other overlay elements
  const overlays = document.querySelectorAll('[id*="overlay"]');
  overlays.forEach(overlay => {
    if (overlay.textContent && overlay.textContent.includes('ResizeObserver')) {
      overlay.style.display = 'none';
    }
  });
};

// Enhanced console error override for ResizeObserver
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

console.error = (...args) => {
  const message = args[0];
  if (message && typeof message === 'string') {
    if (message.includes('ResizeObserver loop limit exceeded') || 
        message.includes('ResizeObserver loop completed with undelivered notifications')) {
      suppressResizeObserverError();
      return;
    }
  }
  originalConsoleError.apply(console, args);
};

console.warn = (...args) => {
  const message = args[0];
  if (message && typeof message === 'string') {
    if (message.includes('ResizeObserver loop') || 
        message.includes('ResizeObserver')) {
      suppressResizeObserverError();
      return;
    }
  }
  originalConsoleWarn.apply(console, args);
};

// Global error handler for ResizeObserver
window.addEventListener('error', (e) => {
  if (e.message && e.message.includes('ResizeObserver')) {
    e.preventDefault();
    suppressResizeObserverError();
    return false;
  }
});

// Handle unhandled promise rejections that might contain ResizeObserver errors
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
    e.preventDefault();
    suppressResizeObserverError();
  }
});

const selectStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    position: 'fixed',
    maxHeight: '200px',
    overflow: 'auto',
  }),
  menuPortal: (base) => ({ 
    ...base, 
    zIndex: 9999,
    position: 'fixed',
  }),
  control: (provided) => ({
    ...provided,
    minHeight: '38px',
    maxHeight: '38px',
    overflow: 'hidden',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
    maxHeight: '34px',
    overflow: 'hidden',
  }),
  multiValue: (provided) => ({
    ...provided,
    maxHeight: '30px',
    overflow: 'hidden',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    maxHeight: '28px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '36px',
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '200px',
    padding: 0,
  }),
};

// Custom components to show full text on hover via native tooltip
const Option = (props) => {
  const { data } = props;
  return (
    <components.Option {...props} innerProps={{ ...props.innerProps, title: data.label }}>
      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }} title={data.label}>
        {data.label}
      </span>
    </components.Option>
  );
};

const MultiValueLabel = (props) => {
  const { data } = props;
  return (
    <components.MultiValueLabel {...props} innerProps={{ ...props.innerProps, title: data.label }}>
      {data.label}
    </components.MultiValueLabel>
  );
};

// Custom dropdown indicator (center aligned icon, minimal inline styling)
const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', marginTop: 7 }}>
      <img src={dropdownIcon} alt="dropdown" style={{ width: 25, height: 25, display: 'block' }} />
    </div>
  </components.DropdownIndicator>
);

const selectComponents = { Option, MultiValueLabel, DropdownIndicator };

/**
 * DistrictSchoolSelector
 * - Left: School District (multi-select, async search)
 * - Right: High School (multi-select, independent search)
 *
 * Props:
 * - onDistrictsChange(districtObjects[])
 * - onSchoolsChange(schoolObjects[])
 * - districts: externally controlled selected districts
 * - schools: externally controlled selected schools
 * - showOnlyDistrict: show only district selector
 * - showOnlySchool: show only school selector
 */
const DistrictSchoolSelector = ({
  onDistrictsChange,
  onSchoolsChange,
  initialDistricts = [],
  initialSchools = [],
  districts,
  schools,
  showOnlyDistrict = false,
  showOnlySchool = false,
}) => {
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [selectedDistricts, setSelectedDistricts] = useState(initialDistricts);
  

  // Cleanup function to clear timeouts and suppress errors on unmount
  useEffect(() => {
    return () => {
      clearTimeout(window.districtSearchTimeout);
      clearTimeout(window.schoolSearchTimeout);
      suppressResizeObserverError();
    };
  }, []);
  
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [schoolQuery, setSchoolQuery] = useState("");
  const [selectedSchools, setSelectedSchools] = useState(initialSchools);
  // control input text for district to allow clearing after selection
  const [districtQuery, setDistrictQuery] = useState("");
  

  const loadDistricts = useCallback((query = "") => {
    if (districtLoading) return Promise.resolve();

    setDistrictLoading(true);
    let cancelled = false;

    const trimmed = (query || "").trim();
    const request = QueryService.getDistrictList(trimmed.length > 0 ? trimmed : undefined)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.objects || res?.data?.object || [];
        const opts = (Array.isArray(list) ? list : []).map((d) => ({
          label: d?.name || d?.district || "",
          value: String(d?.id),
          raw: d,
        }));
        setDistrictOptions(opts);
      })
      .catch(() => !cancelled && setDistrictOptions([]))
      .finally(() => !cancelled && setDistrictLoading(false));

    return request;
  }, [districtLoading]);

  const loadHighSchools = useCallback(async (query = "") => {
    if (schoolLoading) return Promise.resolve();

    setSchoolLoading(true);
    try {
      let res;
      const trimmed = query.trim();
      // Always hit search endpoint for any non-empty input (including single char)
      if (trimmed.length > 0) {
        res = await QueryService.getHighSchoolName(trimmed);
      } else {
        // Empty input returns full list
        res = await QueryService.getHighSchoolList();
      }
      const arr = res?.data?.objects || res?.data?.object || [];
      const opts = (Array.isArray(arr) ? arr : []).map((s) => ({
        label: s?.name,
        value: String(s?.id),
        raw: s,
      }));
      // Preserve already selected schools even if they are not in the
      // current options (react-select can render selected values not in options)
      setSchoolOptions(opts);
    } catch (e) {
      setSchoolOptions([]);
    } finally {
      setSchoolLoading(false);
    }
  }, [schoolLoading]);

  // Emit selected schools up (with ref to prevent infinite loops)
  const previousSchoolsRef = useRef();
  useEffect(() => {
    const payload = selectedSchools.map((s) => s.raw).filter(Boolean);
    const payloadStr = JSON.stringify(payload.map(p => p?.id).sort());
    
    if (previousSchoolsRef.current !== payloadStr) {
      previousSchoolsRef.current = payloadStr;
      onSchoolsChange?.(payload);
    }
  }, [selectedSchools, onSchoolsChange]);

  // Emit selected districts up (with ref to prevent infinite loops)
  const previousDistrictsRef = useRef();
  useEffect(() => {
    const payload = selectedDistricts.map((d) => d.raw).filter(Boolean);
    const payloadStr = JSON.stringify(payload.map(p => p?.id).sort());
    
    if (previousDistrictsRef.current !== payloadStr) {
      previousDistrictsRef.current = payloadStr;
      onDistrictsChange?.(payload);
    }
  }, [selectedDistricts, onDistrictsChange]);

  // Sync from parent when external schools change (e.g., chip removal)
  const previousExternalSchoolsRef = useRef();
  useEffect(() => {
    if (!schools) return;
    
    const externalStr = JSON.stringify((Array.isArray(schools) ? schools : []).map(s => s?.id).sort());
    if (previousExternalSchoolsRef.current === externalStr) return;
    
    previousExternalSchoolsRef.current = externalStr;
    
    // Map incoming plain school objects to our react-select option shape
    const next = (Array.isArray(schools) ? schools : []).map((s) => ({
      label: s?.name,
      value: String(s?.id),
      raw: s,
    }));
    setSelectedSchools(next);
  }, [schools]);

  // Sync from parent when external districts change
  const previousExternalDistrictsRef = useRef();
  useEffect(() => {
    if (!districts) return;
    
    const externalStr = JSON.stringify((Array.isArray(districts) ? districts : []).map(d => d?.id).sort());
    if (previousExternalDistrictsRef.current === externalStr) return;
    
    previousExternalDistrictsRef.current = externalStr;
    
    const next = (Array.isArray(districts) ? districts : []).map((d) => ({
      label: d?.name || d?.district || "",
      value: String(d?.id),
      raw: d,
    }));
    setSelectedDistricts(next);
  }, [districts]);

  // If showing individual selectors, return just the select without wrapper
  if (showOnlyDistrict) {
    return (
      <Select
        className="react-select-container"
        classNamePrefix="react-select"
        placeholder="Select districts"
        inputValue={districtQuery}
        components={selectComponents}
        onMenuOpen={() => {
          // Always reset to full list when opening dropdown
          setDistrictQuery("");
          loadDistricts("");
        }}
        options={districtOptions}
        value={selectedDistricts}
        isMulti
        isLoading={districtLoading}
        loadingMessage={() => "Loading districts..."}
        noOptionsMessage={() => districtLoading ? "Loading districts..." : "No districts found"}
        onChange={(vals) => {
          setSelectedDistricts(vals || []);
          setDistrictQuery("");
        }}
        styles={selectStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuShouldBlockScroll={true}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={true}
        blurInputOnSelect={false}
        captureMenuScroll={false}
        onInputChange={(val, meta) => {
          if (meta.action === 'input-change') {
            setDistrictQuery(val);
            const q = val.trim();
            clearTimeout(window.districtSearchTimeout);
            window.districtSearchTimeout = setTimeout(() => {
              // Always query with current input; empty shows full list
              loadDistricts(q);
            }, 300);
          }
        }}
      />
    );
  }

  if (showOnlySchool) {
    return (
      <Select
        className="react-select-container"
        classNamePrefix="react-select"
        placeholder="Select high schools"
        isDisabled={false}
        components={selectComponents}
        onMenuOpen={() => {
          // Always reset to full list when opening dropdown
          setSchoolQuery("");
          loadHighSchools("");
        }}
        inputValue={schoolQuery}
        onInputChange={(val, meta) => {
          if (meta.action === 'input-change') {
            setSchoolQuery(val);
            const q = val.trim();
            clearTimeout(window.schoolSearchTimeout);
            window.schoolSearchTimeout = setTimeout(() => {
              loadHighSchools(q); // pass current input (including 1 char)
            }, 300);
          }
        }}
        options={schoolOptions}
        value={selectedSchools}
        isMulti
        isLoading={schoolLoading}
        loadingMessage={() => "Loading schools..."}
        noOptionsMessage={() => schoolLoading ? "Loading schools..." : "No schools found"}
        onChange={(vals) => {
          setSelectedSchools(vals || []);
          setSchoolQuery("");
        }}
        styles={selectStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuShouldBlockScroll={true}
        menuShouldScrollIntoView={false}
        closeMenuOnScroll={true}
        blurInputOnSelect={false}
        captureMenuScroll={false}
      />
    );
  }

  return (
    <div className="col-md-12 row" style={{ marginTop: 10 }}>
      <div className="col-md-6">
        <div className="query-card">
          <div className="query-title-containers">
            <p className="bold" style={{ marginBottom: 0 }}>School District</p>
          </div>
          <div className="query-card__content">
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Select districts"
              inputValue={districtQuery}
              components={selectComponents}
              onMenuOpen={() => {
                // Always reset to full list when opening dropdown
                setDistrictQuery("");
                loadDistricts("");
              }}
              options={districtOptions}
              value={selectedDistricts}
              isMulti
              isLoading={districtLoading}
              loadingMessage={() => "Loading districts..."}
              noOptionsMessage={() => districtLoading ? "Loading districts..." : "No districts found"}
              onChange={(vals) => {
                setSelectedDistricts(vals || []);
                // clear typed characters after selection
                setDistrictQuery("");
              }}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuShouldBlockScroll={true}
              menuShouldScrollIntoView={false}
              closeMenuOnScroll={true}
              blurInputOnSelect={false}
              captureMenuScroll={false}
              onInputChange={(val, meta) => {
                if (meta.action === 'input-change') {
                  // Debounce API calls to prevent excessive requests
                  setDistrictQuery(val);
                  const q = val.trim();
                  clearTimeout(window.districtSearchTimeout);
                  window.districtSearchTimeout = setTimeout(() => {
                    // Always query with current input; empty shows full list
                    loadDistricts(q);
                  }, 300);
                }
              }}
            />
          </div>
        </div>
      </div>

      <div className="col-md-6">
        <div className="query-card">
          <div className="query-title-containers">
            <p className="bold" style={{ marginBottom: 0 }}>High School</p>
          </div>
          <div className="query-card__content">
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Select high schools"
              isDisabled={false}
              components={selectComponents}
              onMenuOpen={() => {
                // Always reset to full list when opening dropdown
                setSchoolQuery("");
                loadHighSchools("");
              }}
              inputValue={schoolQuery}
              onInputChange={(val, meta) => {
                if (meta.action === 'input-change') {
                  setSchoolQuery(val);
                  // Debounce API calls to prevent excessive requests
                  const q = val.trim();
                  clearTimeout(window.schoolSearchTimeout);
                  window.schoolSearchTimeout = setTimeout(() => {
                    loadHighSchools(q); // always pass input
                  }, 300);
                }
              }}
              options={schoolOptions}
              value={selectedSchools}
              isMulti
              isLoading={schoolLoading}
              loadingMessage={() => "Loading schools..."}
              noOptionsMessage={() => schoolLoading ? "Loading schools..." : "No schools found"}
              onChange={(vals) => {
                setSelectedSchools(vals || []);
                // clear typed characters after selection
                setSchoolQuery("");
              }}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuShouldBlockScroll={true}
              menuShouldScrollIntoView={false}
              closeMenuOnScroll={true}
              blurInputOnSelect={false}
              captureMenuScroll={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistrictSchoolSelector;
